import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import SectionPicker from '../../reusable/SectionPicker/SectionPicker';
import { useShellAsideControl } from '../../layout/ShellAside';
import { t } from '../../i18n/i18n';
import './VerificationAside.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The section nav. "Verification" holds the KYC and document tabs beneath it. */
export const SECTIONS = [
  { key: 'payment', to: '/verification', label: 'Payment Verification', icon: 'nav-payments', end: true },
  { key: 'identity', to: '/verification/identity', label: 'Verification', icon: 'nav-verification' },
  { key: 'contracts', to: '/verification/contracts', label: 'Contracts list', icon: 'nav-certificates' },
];

/** The rule NavLink applies, so the fill can't disagree with which row is lit. */
const isCurrent = (section, pathname) =>
  section.end
    ? pathname === section.to
    : pathname === section.to || pathname.startsWith(`${section.to}/`);

/**
 * The Payment & Verification section nav, drawn as the Affiliate and Profile
 * asides are (1:16694): flush against the rail at full height, published onto
 * the shell rather than rendered in the page — see `useShellAside` — which is
 * why `/verification/*` joins the rail routes.
 */
export default function VerificationAside() {
  const { pathname } = useLocation();
  const { isAsideCollapsed, toggleAside } = useShellAsideControl();
  const [tip, setTip] = useState(null);

  const isRail = Boolean(isAsideCollapsed);

  useEffect(() => {
    if (!isRail) setTip(null);
  }, [isRail]);

  const peek = (event) => {
    if (!isRail) return;
    const row = event.target.closest?.('.verification-aside__link');
    const label = row?.dataset?.label;
    if (!label) return;

    const rect = row.getBoundingClientRect();
    setTip({ label, x: rect.right + 10, y: rect.top + rect.height / 2 });
  };

  const index = SECTIONS.findIndex((section) => isCurrent(section, pathname));

  /* Where it rests if a route under /verification ever falls outside this
     list: it fades where it stands instead of sliding back to the first row. */
  const [resting, setResting] = useState(index < 0 ? 0 : index);

  useEffect(() => {
    if (index >= 0) setResting(index);
  }, [index]);

  const at = index < 0 ? resting : index;

  return (
    <aside className={`verification-aside shell__aside${isRail ? ' verification-aside--rail' : ''}`}>
      <div className="verification-aside__head">
        <div className="verification-aside__head-copy">
          <h2 className="verification-aside__title">{t('Payment & Verification')}</h2>
        </div>
        <button
          type="button"
          className="verification-aside__collapse"
          onClick={toggleAside}
          aria-expanded={!isRail}
          aria-label={isRail ? t('Expand verification menu') : t('Collapse verification menu')}
          title={isRail ? t('Expand verification menu') : t('Collapse verification menu')}
        >
          <span className="icon" style={icon('sidebar-collapse')} />
        </button>
      </div>

      {/* The phone's version of the same list — CSS decides which one is live. */}
      <SectionPicker
        sections={SECTIONS.map((section) => ({ ...section, label: t(section.label) }))}
        label={t('Verification section')}
      />

      <nav
        className="verification-aside__nav"
        onMouseOver={peek}
        onMouseOut={() => setTip(null)}
        onFocus={peek}
        onBlur={() => setTip(null)}
        onScroll={() => setTip(null)}
      >
        <span
          className={`verification-aside__fill${index >= 0 ? ' is-on' : ''}`}
          style={{ '--fill-i': at }}
          aria-hidden="true"
        />

        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end={section.end}
            data-label={t(section.label)}
            title={isRail ? t(section.label) : undefined}
            className={({ isActive }) =>
              `verification-aside__link${isActive ? ' is-active' : ''}`
            }
          >
            <span className="icon verification-aside__icon" style={icon(section.icon)} />
            <span className="verification-aside__label">{t(section.label)}</span>
          </NavLink>
        ))}
      </nav>

      {isRail && tip &&
        createPortal(
          <div
            className="rail-tip"
            style={{ top: `${tip.y}px`, left: `${tip.x}px` }}
            aria-hidden="true"
          >
            {tip.label}
          </div>,
          document.body,
        )}
    </aside>
  );
}
